// Terms of Service Page (Dark Theme)
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';

function Terms() {
  return (
    <div className="min-h-screen bg-[#0d1117] relative overflow-hidden">
      {/* Gradient background overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative px-6 py-4 sticky top-0 bg-[#0d1117]/80 backdrop-blur-md z-50 border-b border-white/10">
        <nav className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
              <GoldenSpiral className="w-6 h-6" color="white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white">VinciKit</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </nav>
      </header>

      <div className="relative max-w-3xl mx-auto py-12 px-6">
        <h1 className="text-3xl font-bold text-white mb-8 tracking-tight">Terms of Service</h1>

        <div className="space-y-8">
          <p className="text-gray-500 text-sm">Last updated: December 2025</p>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-400 leading-relaxed">
              By accessing or using VinciKit, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-400 leading-relaxed">
              VinciKit is an AI-powered tool that helps users discover and configure optimal AI tools and API stacks for their projects.
              Our service provides recommendations based on your project goals, expertise, and budget.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              To use VinciKit, you must create an account using Google authentication. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li>Maintaining the confidentiality of your account</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us of any unauthorized use</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">4. Acceptable Use</h2>
            <p className="text-gray-400 leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li>Use the service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service</li>
              <li>Use automated means to access the service without permission</li>
              <li>Reverse engineer or attempt to extract source code</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">5. AI-Generated Recommendations</h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              Our recommendations are generated using AI models (Claude and Gemini). While we strive for accuracy:
            </p>
            <ul className="list-disc pl-6 text-gray-400 space-y-2">
              <li>Recommendations are suggestions, not guarantees</li>
              <li>You are responsible for evaluating and validating any tools or APIs before use</li>
              <li>We are not liable for decisions made based on our recommendations</li>
              <li>Tool availability, pricing, and features may change without notice</li>
            </ul>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">6. Intellectual Property</h2>
            <p className="text-gray-400 leading-relaxed">
              The VinciKit service, including its design, features, and content, is owned by us and protected by intellectual property laws.
              You retain ownership of any data you provide to the service.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-gray-400 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
              INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-400 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">9. Changes to Terms</h2>
            <p className="text-gray-400 leading-relaxed">
              We may modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">10. Termination</h2>
            <p className="text-gray-400 leading-relaxed">
              We reserve the right to suspend or terminate your access to the service at any time, for any reason, without notice.
            </p>
          </section>

          <section className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
            <h2 className="text-xl font-semibold text-white mb-4">11. Contact</h2>
            <p className="text-gray-400 leading-relaxed">
              For questions about these terms, please contact us at:{' '}
              <a href="mailto:support@vincikit.com" className="text-purple-400 hover:text-purple-300 transition-colors">
                support@vincikit.com
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative px-6 py-10 border-t border-white/10 bg-[#0d1117]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-semibold text-white">VinciKit</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">Privacy</Link>
            <Link to="/pricing" className="text-sm text-gray-500 hover:text-white transition-colors">Pricing</Link>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 VinciKit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Terms;
